import { Capacitor } from '@capacitor/core'
import { log } from './logger'

const STORAGE_KEY = 'beacon_shake_to_report'
const listeners = new Set<() => void>()

type PermissionedMotionEvent = typeof DeviceMotionEvent & {
  requestPermission?: () => Promise<'granted' | 'denied'>
}

function motionEvent(): PermissionedMotionEvent | null {
  return typeof DeviceMotionEvent === 'undefined' ? null : (DeviceMotionEvent as PermissionedMotionEvent)
}

/** A touch device with a motion sensor API — desktops expose the API but never shake. */
export function supportsShake(): boolean {
  return motionEvent() !== null && navigator.maxTouchPoints > 0
}

/** iOS gates motion events behind a permission that must be requested from a tap. */
export function needsMotionPermission(): boolean {
  return typeof motionEvent()?.requestPermission === 'function'
}

/**
 * Must run inside a user gesture — on a touch screen that's `click` or
 * `touchend`; WebKit doesn't count `touchstart`/`pointerdown` as one and
 * rejects ('error'). The native iOS shell grants it silently (Capacitor's
 * WKUIDelegate answers .grant); Safari shows a prompt the first time.
 */
export async function requestMotionPermission(): Promise<'granted' | 'denied' | 'error'> {
  const request = motionEvent()?.requestPermission
  if (!request) return 'granted'
  try {
    return (await request()) === 'granted' ? 'granted' : 'denied'
  } catch (err) {
    log('rage-shake', 'motion permission request failed', { message: err instanceof Error ? err.message : String(err) })
    return 'error'
  }
}

// On by default, except iOS Safari, where turning it on means a permission
// prompt — that should come from the user flipping the setting, not a stray tap.
function defaultEnabled(): boolean {
  return Capacitor.isNativePlatform() || !needsMotionPermission()
}

export function isShakeToReportEnabled(): boolean {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw === 'on') return true
    if (raw === 'off') return false
  } catch {
    // Storage blocked: fall through to the platform default.
  }
  return defaultEnabled()
}

export function setShakeToReportEnabled(enabled: boolean) {
  try {
    localStorage.setItem(STORAGE_KEY, enabled ? 'on' : 'off')
  } catch {
    // Not remembered past this visit; the choice still applies now.
  }
  listeners.forEach(l => l())
}

export function subscribeShakeToReport(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
